import React, { useEffect, useState } from "react";
import styled from "styled-components";
import pokeApi, { PokemonDetails } from "../services/pokeApi";
import { animatedSpriteUrl, gb, spriteUrl, statLabels, typeColors } from "../styles/theme";

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
`;

const Name = styled.h1`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Number = styled.span`
  font-size: 8px;
  opacity: 0.8;
  flex-shrink: 0;
`;

const Genus = styled.p`
  font-size: 7px;
  color: ${gb.mid};
  margin-bottom: 6px;
  text-transform: uppercase;
`;

const Stage = styled.div`
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

const SpriteWell = styled.div`
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${gb.light};
  border: 2px solid ${gb.dark};
  box-shadow: inset 2px 2px 0 rgba(15, 56, 15, 0.2);
`;

const Sprite = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Types = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const TypeBadge = styled.span<{ $type: string }>`
  background: ${(props) => typeColors[props.$type] ?? gb.dark};
  color: #1a1a1a;
  padding: 4px 6px;
  font-size: 7px;
  text-transform: uppercase;
  border: 2px solid ${gb.dark};
  text-shadow: 1px 1px 0 rgba(255, 255, 255, 0.25);
`;

const Measure = styled.div`
  font-size: 7px;
  line-height: 1.6;
  text-transform: uppercase;
`;

const Flavor = styled.p`
  font-size: 7px;
  line-height: 1.7;
  background: ${gb.light};
  border: 2px solid ${gb.dark};
  padding: 6px;
  margin-bottom: 8px;
  box-shadow: inset 2px 2px 0 rgba(15, 56, 15, 0.15);
`;

const Stats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: auto;
`;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr 24px;
  align-items: center;
  gap: 6px;
`;

const StatLabel = styled.span`
  font-size: 7px;
`;

const BarTrack = styled.div`
  height: 10px;
  background: ${gb.mid};
  border: 2px solid ${gb.dark};
  image-rendering: pixelated;
`;

const BarFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${(props) => props.$pct}%;
  background: ${gb.dark};
`;

const StatValue = styled.span`
  font-size: 7px;
  text-align: right;
`;

const Idle = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  font-size: 8px;
  line-height: 1.8;
`;

const ErrorText = styled.div`
  font-size: 8px;
  text-align: center;
  padding: 16px;
  line-height: 1.8;
`;

interface PokemonDetailProps {
  pokemonId: number | null;
  pokemonName?: string;
}

const formatMeasure = (height: number, weight: number) => {
  const meters = (height / 10).toFixed(1);
  const kilos = (weight / 10).toFixed(1);
  return { meters, kilos };
};

const PokemonDetail: React.FC<PokemonDetailProps> = ({
  pokemonId,
  pokemonName,
}) => {
  const [pokemon, setPokemon] = useState<PokemonDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pokemonId == null) {
      setPokemon(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const details = await pokeApi.getPokemonDetails(pokemonId);
        if (!cancelled) {
          setPokemon(details);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load entry");
          setPokemon(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [pokemonId]);

  if (pokemonId == null) {
    return (
      <Idle>
        <div>POKéDEX v.01</div>
        <div>CHOOSE A<br />POKéMON</div>
      </Idle>
    );
  }

  const entry = pokemon?.id === pokemonId ? pokemon : null;

  if (error && !entry) {
    return <ErrorText>{error}</ErrorText>;
  }

  const name = entry?.name ?? pokemonName ?? "POKéMON";
  const sprite = entry?.animatedSprite ?? animatedSpriteUrl(pokemonId);
  const fallbackSprite = entry?.sprite ?? spriteUrl(pokemonId);
  const measures = entry ? formatMeasure(entry.height, entry.weight) : null;

  return (
    <Container>
      <Header>
        <Name>{name}</Name>
        <Number>#{String(pokemonId).padStart(3, "0")}</Number>
      </Header>
      <Genus>{entry?.genus ?? (loading ? "READING..." : "")}</Genus>
      <Stage>
        <SpriteWell>
          <Sprite
            src={sprite}
            alt={name}
            onError={(event) => {
              event.currentTarget.src = fallbackSprite;
            }}
          />
        </SpriteWell>
        <Meta>
          {entry ? (
            <>
              <Types>
                {entry.types.map((type) => (
                  <TypeBadge key={type} $type={type}>
                    {type}
                  </TypeBadge>
                ))}
              </Types>
              {measures && (
                <Measure>
                  HT {measures.meters} m
                  <br />
                  WT {measures.kilos} kg
                </Measure>
              )}
            </>
          ) : (
            <Measure>LOADING DATA</Measure>
          )}
        </Meta>
      </Stage>
      {entry?.flavorText && <Flavor>{entry.flavorText}</Flavor>}
      {entry && (
        <Stats>
          {entry.stats.map((stat) => (
            <StatRow key={stat.name}>
              <StatLabel>{statLabels[stat.name] ?? stat.name}</StatLabel>
              <BarTrack>
                <BarFill $pct={Math.min(100, (stat.value / 180) * 100)} />
              </BarTrack>
              <StatValue>{stat.value}</StatValue>
            </StatRow>
          ))}
        </Stats>
      )}
    </Container>
  );
};

export default PokemonDetail;
